import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FileText, Users, Clock, ChevronDown, ChevronRight, Check, Hourglass, Plus, X, LogOut, MessageSquareDashed, AlertTriangle, Link2, ExternalLink } from 'lucide-react'
import { useAddParty } from '../../hooks/useNegotiations'
import { readDocumentBinding, type DocumentBinding } from '../../hooks/useCustomXmlParts'
import { PartyRow } from './PartyRow'
import type { PartyInput } from './PartyRow'
import type { NegotiationDetail, ActiveUser } from '../../services/negotiationsApi'
import { MAIN_APP_URL } from '../../config'

interface SendBackBannerProps {
  negotiation: NegotiationDetail
  actorId?: string
  showToast?: (type: 'success' | 'error' | 'info', message: string) => void
  /** Word comment sync state — shown inline in the info row */
  wordSync?: { isSupported: boolean; isActive: boolean; syncedCount: number }
  /** Active users viewing this negotiation */
  activeUsers?: ActiveUser[]
}

function getDeadlineInfo(deadline: string | null): {
  label: string
  colorClass: string
  isExpired: boolean
  isSoon: boolean
  urgencyLevel: 'normal' | 'soon' | 'expired'
  countdown?: string
} | null {
  if (!deadline) return null
  const d = new Date(deadline)
  const now = new Date()
  const diffMs = d.getTime() - now.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)

  if (diffMs <= 0) {
    return { label: d.toLocaleDateString(), colorClass: 'text-error-600 dark:text-error-400', isExpired: true, isSoon: false, urgencyLevel: 'expired' }
  }
  if (diffHours <= 24) {
    const h = Math.floor(diffHours)
    const m = Math.floor((diffHours - h) * 60)
    const countdown = `${h}t ${m}m`
    return { label: countdown, colorClass: 'text-warning-600 dark:text-warning-400', isExpired: false, isSoon: true, urgencyLevel: 'soon', countdown }
  }
  return { label: d.toLocaleDateString(), colorClass: 'text-neutral-500 dark:text-neutral-400', isExpired: false, isSoon: false, urgencyLevel: 'normal' }
}

const EMPTY_PARTY: PartyInput = { label: '', tagName: '', role: 'CUSTOMER' }

export function SendBackBanner({ negotiation, actorId, showToast, wordSync, activeUsers = [] }: SendBackBannerProps) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [newParty, setNewParty] = useState<PartyInput>({ ...EMPTY_PARTY })
  const deadlineInfo = getDeadlineInfo(negotiation.deadline)
  const addPartyMut = useAddParty()

  const existingTags = useMemo(
    () => negotiation.parties.map((p) => p.tag_name),
    [negotiation.parties],
  )

  // 7b: Read document binding from Custom XML
  const [docBinding, setDocBinding] = useState<DocumentBinding | null>(null)
  useEffect(() => {
    readDocumentBinding().then(setDocBinding).catch(() => {})
  }, [])

  const canAdd = !!(newParty.actorId || newParty.email) && newParty.label.trim() && newParty.tagName.trim()
    && /^[a-zA-Z0-9_]{1,15}$/.test(newParty.tagName)
    && !existingTags.some((t) => t.toLowerCase() === newParty.tagName.toLowerCase())
    && !addPartyMut.isPending

  const handleAddParty = async () => {
    if (!canAdd) return
    try {
      await addPartyMut.mutateAsync({
        negotiationId: negotiation.id,
        actorId: newParty.actorId,
        email: newParty.email,
        partyLabel: newParty.label.trim(),
        tagName: newParty.tagName.trim(),
        role: newParty.role,
      })
      showToast?.('success', t('negotiate.partyAdded'))
      setNewParty({ ...EMPTY_PARTY })
      setShowAddForm(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      showToast?.('error', msg)
    }
  }

  return (
    <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl shadow-sm px-3 py-2 space-y-1">
      {/* Title row */}
      <div className="flex items-center gap-2">
        <FileText size={12} className="text-primary-600 dark:text-primary-400 shrink-0" />
        <span className="text-[11px] font-medium text-primary-800 dark:text-primary-200 truncate flex-1">
          {negotiation.title}
        </span>
        {docBinding?.koblink_id && (
          <span className="flex items-center gap-0.5 text-[8px] font-mono text-primary-400 dark:text-primary-600 shrink-0" title={`KOBLINK: ${docBinding.koblink_id}`}>
            <Link2 size={8} />
            {docBinding.koblink_id.slice(0, 8)}
          </span>
        )}
        {/* Bulk G1: discoverability link to the full negotiation in the
            browser. Task pane can't show every tab the web app does
            (Stats, Issues, etc.) so we make the escape hatch explicit. */}
        <a
          href={`${MAIN_APP_URL}/negotiations/${negotiation.id}`}
          target="_blank"
          rel="noopener noreferrer"
          title={t('negotiate.openInBrowser')}
          aria-label={t('negotiate.openInBrowser')}
          className="shrink-0 p-0.5 text-primary-400 hover:text-primary-600 dark:text-primary-500 dark:hover:text-primary-300 transition-colors"
        >
          <ExternalLink size={11} />
        </a>
      </div>

      {/* Urgent deadline banner — shown only when deadline is soon or expired */}
      {deadlineInfo?.urgencyLevel === 'expired' && (
        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-error-50 dark:bg-error-900/30 border border-error-200 dark:border-error-800 text-error-700 dark:text-error-300 text-[10px] font-medium">
          <AlertTriangle size={12} className="shrink-0" />
          <span>{t('negotiate.deadlineExpiredBanner', { date: deadlineInfo.label })}</span>
        </div>
      )}
      {deadlineInfo?.urgencyLevel === 'soon' && (
        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-warning-50 dark:bg-warning-900/30 border border-warning-200 dark:border-warning-800 text-warning-700 dark:text-warning-300 text-[10px] font-medium">
          <Clock size={12} className="shrink-0" />
          <span>{t('negotiate.deadlineSoonBanner', { time: deadlineInfo.countdown })}</span>
        </div>
      )}

      {/* Compact info row: version · parties · status · deadline */}
      <div className="flex items-center gap-1.5 text-[10px] text-primary-600 dark:text-primary-400 flex-wrap">
        <span className="font-mono font-medium">
          {t('negotiate.currentVersion', { version: negotiation.current_version })}
        </span>
        <span className="text-primary-300 dark:text-primary-700">·</span>
        <button
          onClick={() => setIsExpanded(s => !s)}
          className="flex items-center gap-0.5 hover:text-primary-800 dark:hover:text-primary-200 transition-colors"
        >
          <Users size={10} />
          {negotiation.total_parties}
          {isExpanded ? <ChevronDown size={8} /> : <ChevronRight size={8} />}
        </button>
        <span className="px-1.5 py-px rounded-full bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400 text-[9px] font-medium">
          {t('negotiate.activeNegotiation')}
        </span>
        {deadlineInfo && (
          <>
            <span className="text-primary-300 dark:text-primary-700">·</span>
            <span className={`flex items-center gap-0.5 ${deadlineInfo.colorClass}`}>
              <Clock size={9} />
              {deadlineInfo.label}
            </span>
          </>
        )}
        {wordSync?.isSupported && (
          <>
            <span className="text-primary-300 dark:text-primary-700">·</span>
            <span className="flex items-center gap-0.5 text-neutral-400">
              <MessageSquareDashed size={9} />
              #
              {wordSync.syncedCount > 0 && (
                <span className="text-success-500">{wordSync.syncedCount}</span>
              )}
            </span>
          </>
        )}
        {activeUsers.length > 0 && (
          <>
            <span className="text-primary-300 dark:text-primary-700">·</span>
            <span className="flex items-center gap-0.5" title={activeUsers.map(u => u.display_name).join(', ')}>
              <span className="flex -space-x-0.5">
                {activeUsers.slice(0, 3).map(u => (
                  <span
                    key={u.actor_id}
                    className="inline-block w-3 h-3 rounded-full ring-1 ring-white dark:ring-neutral-900"
                    style={{ backgroundColor: u.color || '#6b7280' }}
                  />
                ))}
              </span>
              <span className="text-[9px] text-neutral-400">
                {activeUsers.length}
              </span>
            </span>
          </>
        )}
      </div>

      {/* Expanded party list */}
      {isExpanded && negotiation.parties.length > 0 && (
        <div className="pt-1 space-y-1 animate-fade-in">
          {negotiation.parties.map(party => (
            <div
              key={party.id}
              className={`flex items-center gap-2 px-1.5 py-1 rounded-lg bg-white/60 dark:bg-neutral-800/40 ${party.has_left ? 'opacity-50' : ''}`}
            >
              {/* Avatar circle with party color */}
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0"
                style={{ backgroundColor: party.color || '#6b7280' }}
              >
                {party.party_label?.charAt(0)?.toUpperCase() || '?'}
              </div>

              {/* Name + tag */}
              <div className="flex-1 min-w-0">
                <span className="text-[11px] font-medium text-neutral-800 dark:text-neutral-200 truncate block">
                  {party.party_label}
                </span>
                {party.tag_name && (
                  <span className="text-[9px] font-mono text-neutral-500 dark:text-neutral-400">
                    #{party.tag_name}
                  </span>
                )}
              </div>

              {/* Role badge */}
              <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 shrink-0">
                {party.role}
              </span>

              {/* Ready / Left status */}
              {party.has_left ? (
                <span title={t('negotiate.partyLeft', 'Forlatt')}><LogOut size={12} className="text-neutral-400 shrink-0" /></span>
              ) : party.is_ready_to_sign ? (
                <Check size={12} className="text-success-600 dark:text-success-400 shrink-0" />
              ) : (
                <Hourglass size={12} className="text-neutral-400 dark:text-neutral-500 shrink-0" />
              )}
            </div>
          ))}

          {/* Add party button / form */}
          {!showAddForm ? (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full flex items-center justify-center gap-1 text-[10px] text-primary-500 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 py-1.5 rounded-lg hover:bg-white/60 dark:hover:bg-neutral-800/40 transition-colors"
            >
              <Plus size={10} />
              {t('negotiate.addPartyToActive')}
            </button>
          ) : (
            <div className="pt-1 space-y-2 animate-fade-in">
              <PartyRow
                party={newParty}
                index={0}
                actorId={actorId}
                existingTags={existingTags}
                onChange={(_i, p) => setNewParty(p)}
                onRemove={() => {
                  setShowAddForm(false)
                  setNewParty({ ...EMPTY_PARTY })
                }}
                canRemove={true}
              />
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleAddParty}
                  disabled={!canAdd}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  {addPartyMut.isPending ? (
                    <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Plus size={10} />
                      {t('negotiate.addPartyToActive')}
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false)
                    setNewParty({ ...EMPTY_PARTY })
                  }}
                  className="p-1.5 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 rounded-lg transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  )
}
